// viewer.controls.thumbnail.js — hover-thumbnail popover above the scrub bar.
// Reuses the 320p preview <video> URL (already loaded for smooth-scrub) by
// instantiating a SECOND hidden <video> we can seek freely without disturbing
// the main drag-scrub preview. On pointermove over .vmc-controls-timeline-wrap
// we seek the hidden video, draw its current frame to the popover canvas, and
// position the popover at the cursor.

import { pointerXToTime, formatTime } from "./viewer.controls.js";

const THUMB_W = 160;
const THUMB_H = 90;
const SEEK_THROTTLE_MS = 30;

export function clampThumbnailX(cursorX, wrapWidth, popoverWidth) {
  const half = popoverWidth / 2;
  if (cursorX < half) return half;
  if (cursorX > wrapWidth - half) return wrapWidth - half;
  return cursorX;
}

export function mountThumbnail(opts) {
  const wrap = opts.wrap;
  const previewSrc = opts.previewSrc;
  const duration = opts.duration ?? (() => 0);
  if (!wrap) throw new Error("mountThumbnail: wrap is required");

  const popover = wrap.querySelector(".vmc-controls-thumbnail");
  const canvas  = popover?.querySelector("canvas");
  const timeLbl = popover?.querySelector(".vmc-controls-thumbnail-time");
  if (!popover || !canvas || !timeLbl) {
    return { dispose() {} };
  }

  // No previewSrc → never show. Stub dispose.
  if (typeof previewSrc !== "string" || previewSrc.length === 0) {
    return { dispose() {} };
  }

  // Hidden source <video> (separate from .vmc-preview so the two don't fight
  // for currentTime). Out-of-flow + zero-size so it never affects layout.
  const src = document.createElement("video");
  src.setAttribute("data-vmc-thumbnail", "");
  src.src = previewSrc;
  src.muted = true;
  src.preload = "auto";
  src.crossOrigin = "anonymous";
  src.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:-9999px;";
  document.body.appendChild(src);

  // A broken preview (missing file on an old HDD bundle, expired presign)
  // must never flash an empty popover: kill the thumbnail for this mount.
  let broken = false;
  const onSrcError = () => { broken = true; popover.hidden = true; };
  src.addEventListener("error", onSrcError, { once: true });

  const ctx = canvas.getContext("2d");
  let pendingSeek = null;
  let lastSeekAt = 0;
  let pendingFlush = null;

  const draw = () => {
    try { ctx.drawImage(src, 0, 0, THUMB_W, THUMB_H); } catch { /* not ready */ }
  };

  const flushSeek = () => {
    pendingFlush = null;
    if (pendingSeek === null) return;
    lastSeekAt = performance.now();
    try { src.currentTime = pendingSeek; } catch { /* not ready */ }
    pendingSeek = null;
  };

  const requestSeek = (t) => {
    pendingSeek = t;
    const elapsed = performance.now() - lastSeekAt;
    if (elapsed < SEEK_THROTTLE_MS) {
      // Schedule a deferred flush so the final cursor position seeks even if
      // the user stops moving before the throttle window expires.
      if (pendingFlush === null) {
        pendingFlush = setTimeout(flushSeek, SEEK_THROTTLE_MS - elapsed);
      }
      return;
    }
    flushSeek();
  };

  src.addEventListener("seeked", draw);

  const onMove = (e) => {
    if (broken) return;
    const rect = wrap.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const dur = duration();
    const t = pointerXToTime(x, rect.width, dur);
    popover.hidden = false;
    const popW = popover.offsetWidth || THUMB_W;
    popover.style.left = `${clampThumbnailX(x, rect.width, popW)}px`;
    timeLbl.textContent = formatTime(t);
    requestSeek(t);
  };
  const onLeave = () => { popover.hidden = true; };

  wrap.addEventListener("pointermove", onMove);
  wrap.addEventListener("pointerleave", onLeave);

  return {
    dispose() {
      if (pendingFlush !== null) {
        clearTimeout(pendingFlush);
        pendingFlush = null;
      }
      wrap.removeEventListener("pointermove", onMove);
      wrap.removeEventListener("pointerleave", onLeave);
      src.removeEventListener("seeked", draw);
      src.removeEventListener("error", onSrcError);
      src.remove();
    },
  };
}
